import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { OriginMark } from '@/components/admin/origin-mark';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { ExportMenu } from '@/components/admin/sessions/export-menu';
import { ReplayPill } from '@/components/admin/sessions/replay-pill';
import { ReviewEntryButton } from '@/components/admin/sessions/review-entry-button';
import { plural } from '@/components/admin/sessions/walk/walk-labels';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import { Button } from '@/components/ui/button';
import { REPLAY_HEAD_LEAD, REPLAY_HEAD_TAIL } from '@/lib/replay-mark';
import { interruptionAuthor } from '@/lib/session-interruption';
import {
  sessionDate,
  sessionStatusLabel,
  sessionStatusTone,
  sessionTime,
} from '@/lib/session-labels';
import { PROD_SESSION_LINE, isProdOrigin } from '@/lib/session-origin';
import type { SessionDetail } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';

/**
 * The head of the parcours (§12.2): where one is, what one is reading, and the
 * two gestures of §12.5.
 *
 * ## The breadcrumb points at `/admin/sessions`
 *
 * It was written against `/admin/journal` while the list of §12.1 was still
 * being built in another worktree, and flipped at the integration of the three
 * screens. `/admin/journal` is not gone - it redirects, keeping its `?session=`
 * (§15) - but a breadcrumb that went through a redirection would put one
 * needless entry in the browser's history on every reading.
 *
 * ## « Relecture » comes first, before « Exporter en »
 *
 * The entry to the review (relecture.md §3.2, issue #187) is the one gesture
 * this head gained: a link on a session that is over, a disabled button with its
 * sentence on one that is not, nothing on an experience with no protocol page
 * (`ReviewEntryButton`, which the conversation mounts too).
 *
 * ## « interrompue à 12:12 par alice » (issue #199, interruption.md §4.4)
 *
 * The subtitle says « terminée à » for any session with an end; an interrupted
 * one says whose gesture ended it, the local part of the email (« jeton
 * partagé » as it stands), and at which hour - the hour of the GESTURE, where
 * the duration of the figures runs to the last activity.
 *
 * ## A replay is marked here, and says so in its subtitle (rejeu.md §12, #193)
 *
 * The violet pill « rejeu · strict » comes after the status, and the subtitle
 * reads « rejouée par Claude, sans lien d'entrée » where an ordinary session
 * says « entrée par le lien ... ». The panel « Rejeu par Claude » is NOT drawn
 * here: it stands under this head and above the figures, mounted by the route
 * (`ReplayPanel`), because it is a block of its own and not a line of the head.
 *
 * ## Exporting is the menu the conversation carries too
 *
 * « Exporter en » (§12.6, issue #173) is ONE component, `ExportMenu`, mounted
 * here and on the conversation: four entries in two groups, and on a session
 * in the trash a disabled button that says why. Its header argues why it is a
 * disclosure rather than `ActionMenu`.
 */

export interface SessionHeadProps {
  readonly detail: SessionDetail;
  /** Opens the confirmation of §12.5. The box itself belongs to the screen. */
  readonly onDelete: () => void;
}

export function SessionHead({ detail, onDelete }: SessionHeadProps) {
  const { session } = detail;
  const replay = session.replay ?? null;
  const name = session.experienceName ?? 'Expérience retirée';
  // « Modèles v5 · Règles v3 » (vitrine.md section 5, issue #192): the versions
  // the session froze at its start. Each is null on a session of before the
  // issue, which froze nothing - and then there is nothing to name.
  const frozenModels = detail.settings?.models ?? null;
  const frozenRules = detail.settings?.rules ?? null;
  // « par alice » on an interrupted session (issue #199, §4.4): the local part of the email.
  const author = interruptionAuthor(session.endedBy);

  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Fil d’Ariane" className="flex flex-wrap gap-2 text-caption text-gray-600">
        <Link to="/admin/sessions" className="text-bleu-nuit hover:underline">
          Sessions
        </Link>
        <span aria-hidden="true">›</span>
        <span>
          {name}, {sessionDate(session.startedAt)}
        </span>
      </nav>

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-bold text-bleu-nuit text-h3">{name}</h1>
            {/* The layer's own pill (§4), not a second one: the two tags carry a
                colour each, and « production v3 » has to read the same here as
                on the card the operator came from. A version nobody can resolve
                any more takes neither colour - it is not a pointer. */}
            {session.experienceVersionNumber === null ? (
              <span className="inline-flex items-center rounded-pill bg-gray-100 px-2.5 py-0.5 font-semibold text-gray-700 text-xs leading-4">
                version illisible
              </span>
            ) : (
              <VersionPill
                entity="session"
                tag={session.entry.tag}
                version={session.experienceVersionNumber}
              />
            )}
            <StatusBadge
              tone={sessionStatusTone(session.status)}
              data-testid={SESSIONS_TEST_IDS.headStatus}
            >
              {sessionStatusLabel(session.status)}
            </StatusBadge>
            {/* The mark « prod » of a session the synchronisation brought up (vitrine.md §8.2, #192). */}
            {isProdOrigin(session) ? <OriginMark testId={SESSIONS_TEST_IDS.headOrigin} /> : null}
            {replay === null ? null : (
              <ReplayPill mode={replay.mode} testId={SESSIONS_TEST_IDS.replayHeadPill} />
            )}
          </div>
          {frozenModels === null && frozenRules === null ? null : (
            <p data-testid="session-reglages-figes" className="text-gray-600 text-sm">
              {frozenModels === null ? null : (
                <>
                  Modèles <b className="font-semibold text-gray-700">v{frozenModels.number}</b>
                </>
              )}
              {frozenModels !== null && frozenRules !== null ? ' · ' : null}
              {frozenRules === null ? null : (
                <>
                  Règles <b className="font-semibold text-gray-700">v{frozenRules.number}</b>
                </>
              )}
              {' · figés au démarrage de la session'}
            </p>
          )}
          <p className="text-gray-600 text-sm">
            Commencée le{' '}
            <b className="font-semibold text-gray-700">{sessionDate(session.startedAt)}</b>
            {session.status === 'interrupted' ? (
              <span data-testid={SESSIONS_TEST_IDS.headInterrupted}>
                , interrompue
                {session.endedAt === null ? null : (
                  <>
                    {' '}
                    à <b className="font-semibold text-gray-700">{sessionTime(session.endedAt)}</b>
                  </>
                )}
                {author === null ? null : (
                  <>
                    {' '}
                    par <b className="font-semibold text-gray-700">{author}</b>
                  </>
                )}
              </span>
            ) : session.endedAt === null ? null : (
              <>
                , terminée à{' '}
                <b className="font-semibold text-gray-700">{sessionTime(session.endedAt)}</b>
              </>
            )}
            {' · '}
            {isProdOrigin(session) ? (
              <>
                <span data-testid={SESSIONS_TEST_IDS.headOriginLine}>{PROD_SESSION_LINE}</span>
                {' · '}
              </>
            ) : null}
            {replay !== null ? (
              <span data-testid={SESSIONS_TEST_IDS.replayHeadLine}>
                <b className="font-semibold text-gray-700">{REPLAY_HEAD_LEAD}</b>
                {REPLAY_HEAD_TAIL}
              </span>
            ) : session.linkName === null ? (
              'sans lien d’entrée'
            ) : (
              <>
                entrée par le lien <b className="font-semibold text-gray-700">{session.linkName}</b>
              </>
            )}
            {' · '}
            <b className="font-semibold text-gray-700">{plural(detail.pages.length, 'page')}</b>{' '}
            {detail.pages.length >= 2 ? 'traversées' : 'traversée'}
            {session.status === 'finished' ? ', la dernière atteinte' : null}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-start gap-2">
          <ReviewEntryButton session={session} />
          <ExportMenu sessionId={session.id} trashed={session.deletedAt !== null} />

          {/* A session already in the trash offers « Restaurer » on its banner
              and nothing here: `trashSession` is idempotent, so a second
              « Supprimer » would be a gesture that changes nothing - including
              the date the banner prints. */}
          {session.deletedAt === null ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-rouge-deep"
              onClick={onDelete}
            >
              Supprimer
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
